import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import {
  DataTableSelectAllChangeEvent,
  DataTableSelectionMultipleChangeEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { SelectedRowsContextType } from '../../../types/context';
import { useDatasetDataRes } from '../../../api/types/types';
import { createSelectedRowsByData } from '../../../utils/createSelectedRowsByData';
import { resolveDeselectedRowIds } from '../../../utils/resolveDeselectedRowIds';
import { resolveSelectAllSelection } from '../../../utils/resolveSelectAllSelection';

type UseSelectedRowsMasterHandlersParams = {
  datasetData: useDatasetDataRes;
  dataKey: string;
  dataRef: MutableRefObject<any[]>;
  selectedRows: any[];
  selectAllChecked: boolean;
  setSelectAllChecked: Dispatch<SetStateAction<boolean>>;
  setSelectedRows: Dispatch<SetStateAction<any[]>>;
  setSelectedVirtualRows: Dispatch<SetStateAction<number>>;
  deselectedRowIdsRef: MutableRefObject<Set<string>>;
  updateDeselectedRowIds: (ids: Set<string>) => void;
};

type UseSelectedRowsMasterHandlersResult = Pick<
  SelectedRowsContextType,
  'processSelectedRowsChange' | 'processSelectAllChange' | 'resetSelectedRows'
>;

export const useSelectedRowsMasterHandlers = ({
  datasetData,
  dataKey,
  dataRef,
  selectedRows,
  selectAllChecked,
  setSelectAllChecked,
  setSelectedRows,
  setSelectedVirtualRows,
  deselectedRowIdsRef,
  updateDeselectedRowIds,
}: UseSelectedRowsMasterHandlersParams): UseSelectedRowsMasterHandlersResult => {
  const processSelectedRowsChange = useCallback(
    (
      checkboxEvent: DataTableSelectionMultipleChangeEvent<DataTableValueArray>,
      selectedVirtualRows: number
    ) => {
      const value: Record<string, any>[] = checkboxEvent.value;
      const totalCount: number | undefined = datasetData?.totalCount;

      const isSelectAllMode: boolean =
        selectAllChecked ||
        selectedVirtualRows > 0 ||
        deselectedRowIdsRef.current.size > 0;

      if (isSelectAllMode && totalCount) {
        const nextDeselectedRowIds = resolveDeselectedRowIds(
          deselectedRowIdsRef.current,
          value,
          dataRef.current,
          dataKey,
          true
        );
        const selection = resolveSelectAllSelection(
          dataRef.current,
          totalCount,
          nextDeselectedRowIds,
          dataKey
        );
        updateDeselectedRowIds(nextDeselectedRowIds);
        setSelectAllChecked(nextDeselectedRowIds.size === 0);
        setSelectedRows(selection.selectedRows);
        setSelectedVirtualRows(selection.selectedVirtualRows);
        return;
      }

      const newSelectedRows = createSelectedRowsByData(
        value,
        dataRef.current,
        dataKey
      );

      if (selectAllChecked) {
        setSelectAllChecked(false);
      }

      if (newSelectedRows.length + selectedVirtualRows === totalCount) {
        setSelectedRows(value);
        setSelectAllChecked(true);
        return;
      }

      // Select specific rows
      setSelectedRows(newSelectedRows);
      return;
    },
    [selectAllChecked, datasetData, dataKey, updateDeselectedRowIds]
  );

  const processSelectAllChange = useCallback(
    (checkboxEvent: DataTableSelectAllChangeEvent) => {
      setSelectAllChecked(checkboxEvent.checked);
      updateDeselectedRowIds(new Set<string>());

      if (checkboxEvent.checked) {
        const newSelectedRows = [...(dataRef.current ?? [])];
        const datasetTotalCount = datasetData?.totalCount ?? 0;

        if (newSelectedRows.length < datasetTotalCount) {
          const numberOfRowsToAdd = datasetTotalCount - newSelectedRows.length;
          setSelectedVirtualRows(numberOfRowsToAdd);
        }

        setSelectedRows(newSelectedRows);
        return;
      }

      setSelectedVirtualRows(0);
      setSelectedRows([]);
    },
    [datasetData, selectedRows, updateDeselectedRowIds]
  );

  const resetSelectedRows = useCallback(() => {
    setSelectedRows([]);
    setSelectedVirtualRows(0);
    setSelectAllChecked(false);
    updateDeselectedRowIds(new Set<string>());
  }, [updateDeselectedRowIds]);

  return {
    processSelectedRowsChange,
    processSelectAllChange,
    resetSelectedRows,
  };
};
